// src/components/DrillCard.js
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import LoopingYouTubePlayer from './LoopingYouTubePlayer';

const DrillCard = ({ drill }) => {
    const [isVideoExpanded, setIsVideoExpanded] = useState(false);
    const [isVideoReady, setIsVideoReady] = useState(false);

    useEffect(() => {
        if (isVideoExpanded) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => { document.body.style.overflow = 'unset'; };
    }, [isVideoExpanded]);

    // Function to extract YouTube video ID from various URL formats, including Shorts
    const getYouTubeId = (url) => {
        if (!url) return { videoId: null, isShort: false };
        const isShort = url.includes('/shorts/');
        // This updated regex handles youtu.be, /watch, /embed, and now /shorts
        const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=|shorts\/)([^#&?]*).*/;
        const match = url.match(regExp);
        const videoId = (match && match[2].length === 11) ? match[2] : null;
        return { videoId, isShort };
    };

    // The data structure from your serializer is nested: RoutineDrill -> Drill
    const { videoId, isShort } = getYouTubeId(drill.drill.youtube_link);
    const start = drill.drill.youtube_start_seconds;
    const end = drill.drill.youtube_end_seconds;

    const formatDuration = (mins, secs) => {
        const m = mins || 0;
        const s = secs || 0;
        if (m > 0 && s > 0) return `${m}m ${s}s`;
        if (m > 0) return `${m}m`;
        if (s > 0) return `${s}s`;
        return '0s';
    };

    return (
        <div className="bg-gray-800 p-4 rounded-lg shadow-md flex justify-between items-center">
            <div className="flex-1 min-w-0">
                <h3 className="text-xl font-bold text-white">
                    {drill.drill.name}
                    {drill.side_modifier && drill.side_modifier !== 'None' ? ` (${drill.side_modifier})` : ''}
                </h3>
                <p className="text-gray-400">{drill.drill.description}</p>
                <div className="flex flex-wrap items-center gap-2 text-sm text-gray-400 mt-3">
                    <span className="bg-gray-700 whitespace-nowrap px-2.5 py-1 rounded">Duration: {formatDuration(drill.duration_minutes, drill.duration_seconds)}</span>
                    <span className="bg-gray-700 whitespace-nowrap px-2.5 py-1 rounded">Rest: {drill.rest_duration_seconds || 0}s</span>
                </div>
            </div>
            {/* This section will now render correctly for Shorts links */}
            {videoId && (
                <>
                    <div
                        onClick={() => setIsVideoExpanded(true)}
                        className="ml-4 flex-shrink-0 relative group cursor-pointer"
                    >
                        <img
                            src={`https://img.youtube.com/vi/${videoId}/0.jpg`}
                            alt="YouTube thumbnail"
                            className="w-28 aspect-video object-cover rounded-md group-hover:opacity-80 transition-opacity"
                        />
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <div className="bg-black bg-opacity-60 rounded-full p-1 opacity-80 group-hover:opacity-100 transition-opacity">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                        </div>
                    </div>

                    {isVideoExpanded && createPortal(
                        <div className="fixed inset-0 z-[100] bg-black bg-opacity-90 flex flex-col items-center justify-center p-4">
                            <button 
                                onClick={() => { setIsVideoExpanded(false); setIsVideoReady(false); }}
                                className="absolute top-4 right-4 text-white bg-gray-800 hover:bg-gray-700 rounded-full p-2 z-50 transition-colors cursor-pointer"
                                aria-label="Close Fullscreen Video"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 cursor-pointer" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                            <div className={`w-full h-full flex items-center justify-center relative overflow-hidden rounded-lg shadow-2xl ${isShort ? 'aspect-[9/16] max-h-[90vh]' : 'aspect-video max-w-[90vw]'}`}>
                                {/* The Curtain */}
                                <div className={`absolute inset-0 z-20 transition-opacity duration-700 pointer-events-none ${isVideoReady ? 'opacity-0' : 'opacity-100'}`}>
                                    <img src={`https://img.youtube.com/vi/${videoId}/0.jpg`} alt="Curtain" className="w-full h-full object-cover" />
                                </div>
                                <LoopingYouTubePlayer
                                    className="w-full h-full scale-[1.05]"
                                    videoId={videoId}
                                    startSeconds={start}
                                    endSeconds={end}
                                    isMuted={false}
                                    showControls={false}
                                    onVideoPlaying={() => setIsVideoReady(true)}
                                />
                            </div>
                        </div>,
                        document.body
                    )}
                </>
            )}
        </div>
    );
};

export default DrillCard;